import cloneDeep from 'lodash/cloneDeep';

import { SystemEvent } from './System/system';
import { MouseCatcher } from './Catchers/mouseCatcher';
import { IScroll } from './Catchers/scrollPath';
import { ScrollCatcher } from './Catchers/scrollCatcher';
import { SystemCamera } from './System/systemCamera';
import { PageBackground } from './Page/pageBackground';
import { PageCameraSection } from './Page/pageCameraSection';
import { PageCamera, IPageCamera } from './Page/pageCamera';
import { IElementBackground } from './Element/elementBackground';
import { ShaderCamera } from './Material/Camera/shaderCamera';
import { ElementsManager } from './Element/elementsManager';
import { InteractionManager } from './Interaction/interactionManager';
import { SectionsManager } from './Section/sectionsManager';
import { InteractionOptions } from './Interaction/interactionElements';
import { IShader } from './Material/Shader/shader';
import { WaterMark } from './Tools/waterMark';
import { MaskMouseTrail } from './Material/Mask/maskMouseTrail';
import { RESPONSIVE_BREAKPOINT } from './Section/section';

import './engine.sass';

export interface PageDesign {
    scroll: IScroll;
    background: IElementBackground[];
    camera: IPageCamera;
    interactions: InteractionOptions[];
    shaders: IShader[];
}

export class Engine {
    body: HTMLElement;

    browser: HTMLElement;

    sections: HTMLElement;

    canvas: HTMLCanvasElement;

    system: SystemCamera;

    mouseCatcher: MouseCatcher;

    scrollCatcher: ScrollCatcher;

    elementsManager: ElementsManager;

    shaderCamera: ShaderCamera;

    interactionManager: InteractionManager;

    sectionsManager: SectionsManager;

    pageBackground: PageBackground;

    pageCamera: PageCamera;

    maskMouseTrail: MaskMouseTrail;

    waterMark: WaterMark;

    with3D = true;

    constructor(body: HTMLElement) {
        this.body = body;
        this.body.classList.add('body');

        this.browser = this.body.querySelector('.browser');
        this.sections = this.body.querySelector('.sections');
        if (!this.browser || !this.sections) throw Error('Missing Browser or section HTMLElement');

        this.canvas = document.createElement('canvas');
        this.body.appendChild(this.canvas);

        try {
            this.system = new SystemCamera(this.canvas);
            this.with3D = this.system.engine.webGLVersion === 2;
            if (this.with3D) this.startWith3D();
        } catch {
            this.with3D = false;
        }
    }

    private startWith3D() {
        //! Keep system settings at the beginning
        // We espacially need the htmlParent to be setted
        // this.system.freeCamera.attachControl(this.system.canvas);
        this.system.setHtmlParent(this.browser);
        // this.system.setAdaptScalingToFps(true);

        this.mouseCatcher = new MouseCatcher(this.system, this.body);
        this.scrollCatcher = new ScrollCatcher(this.system, this.browser);
        this.shaderCamera = new ShaderCamera(this.system, this.body);
        this.maskMouseTrail = new MaskMouseTrail(this.system, this.mouseCatcher);
        this.elementsManager = new ElementsManager(
            this.system,
            this.sections,
            this.mouseCatcher,
            this.scrollCatcher
        );
        this.sectionsManager = new SectionsManager(
            this.system,
            this.sections,
            this.scrollCatcher,
            this.elementsManager
        );
        this.interactionManager = new InteractionManager(
            this.browser,
            this.system,
            this.shaderCamera,
            this.elementsManager,
            this.sectionsManager,
            this.mouseCatcher,
            this.scrollCatcher
        );

        this.pageBackground = new PageBackground(
            this.system,
            this.scrollCatcher
        );

        this.pageCamera = new PageCamera(
            this.browser,
            this.system,
            this.scrollCatcher,
            this.shaderCamera,
            this.maskMouseTrail,
            this.sectionsManager
        );

        this.system.on(SystemEvent.Resize, () => {
            this.checkAfterResize();
        });

        //* For test purppose
        // this.addWaterMark();
    }

    private checkAfterResize() {
        this.checkResponsive();
        this.scrollCatcher.checkHeight();
        this.pageCamera.resetHeight();
        this.sectionsManager.checkSectionsGeometry();
        this.interactionManager.refreshTriggers();
        this.maskMouseTrail.updateTargetResolution();
        this.elementsManager.checkStyle();
        this.pageBackground.updateVertices();
    }

    public checkResponsive() {
        const { clientWidth, classList } = this.browser;
        if (clientWidth > RESPONSIVE_BREAKPOINT) {
            classList.add('responsive');
        } else {
            classList.remove('responsive');
        }
    }

    public addWaterMark() {
        if (!this.with3D) return;
        this.waterMark = new WaterMark(
            this.browser,
            this.system,
            this.mouseCatcher
        );
    }

    public setPageDesign(pageDesign: PageDesign) {
        if (!this.with3D) {
            const pageCameraSection = new PageCameraSection(this.browser);
            const { scrollSnap } = pageDesign.camera;
            pageCameraSection.setScrollSnap(scrollSnap);
            pageCameraSection.setVisibleHTML(true);
        } else {
            //! launchRender first or we have issues with motionBlur
            this.system.launchRender();
            // Clone or values can be replaced
            const newDesign: PageDesign = cloneDeep(pageDesign);
            this.scrollCatcher.options = newDesign.scroll;
            this.elementsManager.shaders = newDesign.shaders;
            this.pageBackground.options = newDesign.background;
            //! pageCamera last or the rendering is transparent
            this.pageCamera.options = newDesign.camera;

            this.shaderCamera.resetBackground();
            this.pageCamera.updateSectionAndScroll();
            this.scrollCatcher.setScrollEvents();
            //! First get all element and then add interactions on it
            this.elementsManager.checkAll();
            this.interactionManager.options = newDesign.interactions;
            //! Check after interaction option setted
            this.interactionManager.checkElementsAttributes();
            this.interactionManager.checkPickableMeshes();
            //! Check scroll after otherwise interaction not setted properly
            this.interactionManager.checkScroll(0);
        }
    }

    public getPageDesign(): PageDesign {
        // Clone or values can be replaced
        return cloneDeep({
            scroll: this.scrollCatcher.options,
            background: this.pageBackground.options,
            camera: this.pageCamera.options,
            interactions: this.interactionManager.options,
            shaders: this.elementsManager.shaders
        });
    }

    public getPageDesignString(): string {
        const pageDesign = this.getPageDesign();
        // Clone or values can be replaced
        return JSON.stringify(pageDesign);
    }
}

window.FuryEngine = Engine;
